import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { StatusBadge } from '@/components/ui/status-badge'
import { ProgressBar } from '@/components/ui/progress-bar'

import type { OngoingProject } from '../types/home.types'

interface ProjectProgressCardProps {
  project: OngoingProject
}

export function ProjectProgressCard({ project }: ProjectProgressCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <div
      onClick={() => void navigate(`/projects/${project.id}`)}
      className={cn(
        'border-border hover:bg-accent cursor-pointer border-t px-4 py-3 transition-colors first:border-t-0',
        'flex items-center gap-4',
        project.urgent && 'border-l-[3px] border-l-red-500'
      )}
    >
      {/* Company Info - Fixed width */}
      <div className="w-[220px] min-w-0 flex-shrink-0">
        <div
          onClick={(e) => {
            e.stopPropagation()
            void navigate(`/companies/${project.companyId}`)
          }}
          className="text-card-foreground hover:text-primary hover:border-primary/40 inline-block cursor-pointer truncate border-b border-dashed border-transparent text-sm font-bold transition-colors"
        >
          {project.company}
        </div>
        <div className="text-muted-foreground mt-0.5 text-xs">
          {t('project.year')} {project.year}{' '}
          {project.workspace && <span>• {project.workspace}</span>}
        </div>
      </div>

      {/* Status Badge - Fixed width */}
      <div className="w-[110px] flex-shrink-0 text-center">
        <StatusBadge status={project.status} size="sm" />
      </div>

      {/* Status Text - Flexible width, fills available space */}
      <div className="text-muted-foreground min-w-0 flex-1 truncate text-xs font-medium">
        {project.statusText}
      </div>

      {/* Progress Bar - Fixed width */}
      <div className="w-[120px] flex-shrink-0">
        <ProgressBar value={project.tasksDone} max={project.tasksTotal} size="sm" />
      </div>

      {/* Deadline - Fixed width */}
      <div className="w-[90px] flex-shrink-0 text-center">
        {project.urgent ? (
          <span className="inline-flex rounded-full border border-[var(--badge-urgent-border)] bg-[var(--badge-urgent-bg)] px-2 py-0.5 text-[10px] font-bold text-[var(--badge-urgent-text)]">
            {t('project.daysLeft', { count: project.daysLeft })}
          </span>
        ) : (
          <span className="text-muted-foreground text-xs">{project.deadline}</span>
        )}
      </div>

      {/* Action Button - Fixed width */}
      <div className="w-[70px] flex-shrink-0 text-right">
        <button
          onClick={(e) => {
            e.stopPropagation()
            void navigate(`/projects/${project.id}`)
          }}
          className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-1 rounded px-3 py-1.5 text-[10px] font-bold transition-colors"
        >
          {t('actions.open')}
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}
